/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import PhoneNumberInput, { renderPhoneNumber } from "components/PhoneNumberInput";
import {
    DEFAULT_COUNTRY_CODE,
    joinPhoneNumber,
    phoneNumberSchema,
    splitPhoneNumber,
} from "util/phoneCountry";
import { useToaster } from "contexts/toasterContext";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, Resolver, useFieldArray, useForm } from "react-hook-form";
import apiService from "services/apiService";
import * as yup from "yup";
import {
    preventSpecialCharKeyAndNumbersDownInTextfield,
    preventSpecialCharKeyDownInTextfield,
} from "util/preventSpecialCharInput";
import { EditButton, EmptyState, InfoCard, InfoField, InfoFieldGrid } from "./components/ProfileInfo";

interface FamilyDetailsProps {
    user: any;
}

interface ChildFormData {
    childName: string;
    childDob: string;
    gender: string;
}

interface FamilyFormData {
    fatherName: string;
    motherName: string;
    maritalStatus: "Single" | "Married" | "Divorced" | "Widowed" | "";
    spouseName: string;
    numberOfChildren: number;
    children: ChildFormData[];
    emergencyContactName: string;
    emergencyContactNumber: string;
    emergencyContactCountryCode: string;
    bloodGroup: string;
}

const CHILD_CELL_TYPOGRAPHY_CLASS =
    "px-4 whitespace-nowrap ![font-family:inherit] ![letter-spacing:normal]";
const CHILD_TH_CLASS = `${CHILD_CELL_TYPOGRAPHY_CLASS} !py-2 !text-xs !font-normal !text-gray-500 dark:!text-gray-400`;
const CHILD_TD_CLASS = `${CHILD_CELL_TYPOGRAPHY_CLASS} !py-2 !text-sm !font-medium !text-gray-800 dark:!text-gray-200`;

const bloodGroupOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const genderOptions = ["Male", "Female"];

const childValidationSchema = yup
    .object({
        childName: yup
            .string()
            .trim()
            .required("Child Name is required")
            .matches(/^[A-Za-z ]+$/, "Only alphabets are allowed"),
        childDob: yup.string().required("Child DOB is required"),
        gender: yup.string().trim().required("Gender is required"),
    })
    .required();

const familyValidationSchema = yup
    .object({
        fatherName: yup
            .string()
            .trim()
            .required("Father Name is required")
            .matches(/^[A-Za-z ]+$/, "Only alphabets are allowed"),
        motherName: yup
            .string()
            .trim()
            .required("Mother Name is required")
            .matches(/^[A-Za-z ]+$/, "Only alphabets are allowed"),
        maritalStatus: yup
            .mixed<FamilyFormData["maritalStatus"]>()
            .oneOf(["Single", "Married", "Divorced", "Widowed"], "Marital Status is required")
            .required("Marital Status is required"),
        spouseName: yup.string().when("maritalStatus", {
            is: "Married",
            then: (schema) =>
                schema
                    .trim()
                    .required("Spouse Name is required")
                    .matches(/^[A-Za-z ]+$/, "Only alphabets are allowed"),
            otherwise: (schema) => schema.transform(() => "").default("").defined(),
        }),
        numberOfChildren: yup
            .number()
            .typeError("Number of Children must be a number")
            .min(0, "Number of Children cannot be negative")
            .required("Number of Children is required"),
        children: yup.array().of(childValidationSchema).when(["maritalStatus", "numberOfChildren"], {
            is: (maritalStatus: string, numberOfChildren: number) =>
                ["Married", "Divorced", "Widowed"].includes(maritalStatus) && Number(numberOfChildren) > 0,
            then: (schema) => schema.min(1, "At least one child detail is required").required(),
            otherwise: (schema) => schema.default([]).required(),
        }),
        emergencyContactName: yup
            .string()
            .trim()
            .required("Emergency Contact Name is required")
            .matches(/^[A-Za-z ]+$/, "Only alphabets are allowed"),
        emergencyContactNumber: phoneNumberSchema("emergencyContactCountryCode", {
            requiredMessage: "Emergency Contact Number is required",
        }),
        emergencyContactCountryCode: yup.string().trim().default(DEFAULT_COUNTRY_CODE),
        bloodGroup: yup.string().trim().required("Blood Group is required"),
    })
    .required();

const defaultChild = (): ChildFormData => ({
    childName: "",
    childDob: "",
    gender: "",
});

const defaultFamilyDetails: FamilyFormData = {
    fatherName: "",
    motherName: "",
    maritalStatus: "",
    spouseName: "",
    numberOfChildren: 0,
    children: [],
    emergencyContactName: "",
    emergencyContactNumber: "",
    emergencyContactCountryCode: DEFAULT_COUNTRY_CODE,
    bloodGroup: "",
};

const hasFamilyData = (details: Partial<FamilyFormData> | null | undefined) => {
    if (!details) return false;
    return [
        details.fatherName,
        details.motherName,
        details.maritalStatus,
        details.spouseName,
        details.emergencyContactName,
        details.emergencyContactNumber,
        details.bloodGroup,
    ].some((value) => typeof value === "string" && value.trim().length > 0);
};

const normalizeFamilyData = (details?: any): FamilyFormData => {
    const children = Array.isArray(details?.children)
        ? details.children.map((child: any) => ({
            childName: child?.childName ?? "",
            childDob: child?.childDob ? new Date(child.childDob).toISOString().split("T")[0] : "",
            gender: child?.gender ?? "",
        }))
        : [];

    const normalizedMaritalStatus = ["Single", "Married", "Divorced", "Widowed"].includes(details?.maritalStatus)
        ? details.maritalStatus
        : "";

    return {
        fatherName: details?.fatherName ?? "",
        motherName: details?.motherName ?? "",
        maritalStatus: normalizedMaritalStatus,
        spouseName: details?.spouseName ?? "",
        numberOfChildren: Number.isFinite(Number(details?.numberOfChildren))
            ? Math.max(Number(details.numberOfChildren), 0)
            : children.length,
        children,
        emergencyContactName: details?.emergencyContactName ?? "",
        emergencyContactNumber: splitPhoneNumber(details?.emergencyContactNumber).nationalNumber,
        emergencyContactCountryCode: splitPhoneNumber(details?.emergencyContactNumber).countryCode,
        bloodGroup: details?.bloodGroup ?? "",
    };
};

const FamilyDetails = ({ user }: FamilyDetailsProps) => {
    const { addToast } = useToaster();
    const [familyDetails, setFamilyDetails] = useState<FamilyFormData | null>(null);
    const [hasPersistedFamilyDetails, setHasPersistedFamilyDetails] = useState(false);
    const [isNotFoundState, setIsNotFoundState] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    const maxDate = new Date().toISOString().split("T")[0];

    const methods = useForm<FamilyFormData>({
        resolver: yupResolver(familyValidationSchema) as Resolver<FamilyFormData>,
        defaultValues: defaultFamilyDetails,
        mode: "onChange",
        reValidateMode: "onChange",
    });

    const {
        handleSubmit,
        watch,
        reset,
        setValue,
        register,
        formState: { errors, isDirty, isSubmitting },
    } = methods;

    const { fields, append, remove } = useFieldArray({
        control: methods.control,
        name: "children",
    });

    const maritalStatus = watch("maritalStatus");
    const numberOfChildren = watch("numberOfChildren");

    const shouldShowSpouseSection = maritalStatus === "Married";
    const canHaveChildren = ["Married", "Divorced", "Widowed"].includes(maritalStatus);
    const shouldShowChildrenSection = canHaveChildren && Number(numberOfChildren || 0) > 0;

    const loadFamilyDetails = async () => {
        setLoading(true);
        setIsNotFoundState(false);
        try {
            const response = await apiService.get("api/familyDetails");
            const apiDetails = normalizeFamilyData(response?.data);

            // KEY FIX: Treat as existing record if it returns an object with an ID, 
            // even if string fields are totally empty.
            if (response?.data?.id || hasFamilyData(apiDetails)) {
                setHasPersistedFamilyDetails(true);
                setFamilyDetails(apiDetails);
                reset(apiDetails);
            } else {
                setHasPersistedFamilyDetails(false);
                const fallback = normalizeFamilyData(user?.familyDetails);
                if (hasFamilyData(fallback)) {
                    setFamilyDetails(fallback);
                    reset(fallback);
                } else {
                    setFamilyDetails(null);
                    reset(defaultFamilyDetails);
                    setIsNotFoundState(true);
                }
            }
        } catch (error) {
            const statusCode =
                error && typeof error === "object" && "response" in error
                    ? (error as { response?: { status?: number } }).response?.status
                    : undefined;

            setHasPersistedFamilyDetails(false);

            if (statusCode === 404) {
                setFamilyDetails(null);
                reset(defaultFamilyDetails);
                setIsNotFoundState(true);
                return;
            }

            const fallback = normalizeFamilyData(user?.familyDetails);
            if (hasFamilyData(fallback)) {
                setFamilyDetails(fallback);
                reset(fallback);
            } else {
                setFamilyDetails(null);
                reset(defaultFamilyDetails);
                setIsNotFoundState(true);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFamilyDetails();
    }, [user?.id]);

    useEffect(() => {
        if (!shouldShowSpouseSection) {
            setValue("spouseName", "", { shouldDirty: true, shouldValidate: true });
        }
    }, [shouldShowSpouseSection, setValue]);

    useEffect(() => {
        if (!canHaveChildren) {
            if (fields.length > 0) {
                for (let i = fields.length - 1; i >= 0; i -= 1) {
                    remove(i);
                }
            }
            if ((numberOfChildren || 0) !== 0) {
                setValue("numberOfChildren", 0, { shouldDirty: true, shouldValidate: true });
            }
            return;
        }

        const desiredChildrenCount = Math.max(Number(numberOfChildren) || 0, 0);
        if (desiredChildrenCount > fields.length) {
            const missingCount = desiredChildrenCount - fields.length;
            for (let i = 0; i < missingCount; i += 1) {
                append(defaultChild());
            }
        }

        if (desiredChildrenCount < fields.length) {
            for (let i = fields.length - 1; i >= desiredChildrenCount; i -= 1) {
                remove(i);
            }
        }
    }, [
        append,
        fields.length,
        canHaveChildren,
        numberOfChildren,
        remove,
        setValue,
    ]);

    const handleAddMode = () => {
        reset(defaultFamilyDetails);
        setFamilyDetails(null);
        setIsAdding(true);
        setIsEditing(false);
    };

    const handleEditMode = () => {
        if (familyDetails) {
            reset(familyDetails);
        }
        setIsEditing(true);
        setIsAdding(false);
    };

    const handleCancel = () => {
        if (familyDetails) {
            reset(familyDetails);
        } else {
            reset(defaultFamilyDetails);
        }
        setIsEditing(false);
        setIsAdding(false);
    };

    const addChildRow = () => {
        append(defaultChild());
        setValue("numberOfChildren", fields.length + 1, { shouldDirty: true, shouldValidate: true });
    };

    const removeChildRow = (index: number) => {
        if (fields.length <= 0) return;
        remove(index);
        setValue("numberOfChildren", Math.max(fields.length - 1, 0), {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const handleNameInput = (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        const { name, value, selectionStart, selectionEnd } = target;

        if (!name) return;

        const capitalizedValue = value.replace(/(^\w{1})|(\s+\w{1})/g, (match) => match.toUpperCase());

        setValue(name as any, capitalizedValue, {
            shouldDirty: true,
            shouldValidate: true,
        });

        requestAnimationFrame(() => {
            target.setSelectionRange(selectionStart, selectionEnd);
        });
    };

    const onSubmit = async (values: FamilyFormData) => {
        const isMarried = values.maritalStatus === "Married";
        const canHaveChildrenSubmit = ["Married", "Divorced", "Widowed"].includes(values.maritalStatus);

        const safeChildren = canHaveChildrenSubmit
            ? values.children.slice(0, Math.max(Number(values.numberOfChildren) || 0, 0)).map((child) => ({
                childName: child.childName.trim(),
                childDob: child.childDob,
                gender: child.gender,
            }))
            : [];

        const payload = {
            fatherName: values.fatherName.trim(),
            motherName: values.motherName.trim(),
            maritalStatus: values.maritalStatus,
            spouseName: isMarried ? values.spouseName.trim() : "",
            numberOfChildren: safeChildren.length,
            children: safeChildren,
            emergencyContactName: values.emergencyContactName.trim(),
            emergencyContactNumber: joinPhoneNumber(
                values.emergencyContactCountryCode,
                values.emergencyContactNumber
            ),
            bloodGroup: values.bloodGroup,
        };

        try {
            if (hasPersistedFamilyDetails) {
                await apiService.put("api/familyDetails", payload);
            } else {
                await apiService.post("api/familyDetails", payload);
            }
            addToast(
                hasPersistedFamilyDetails
                    ? "Family details updated successfully"
                    : "Family details added successfully",
                "success"
            );
            const normalized = normalizeFamilyData(payload);
            setFamilyDetails(normalized);
            reset(normalized);
            setIsEditing(false);
            setIsAdding(false);
            await loadFamilyDetails();
        } catch (error) {
            addToast("Failed to save family details", "error");
        }
    };

    // KEY FIX: Also relies on `hasPersistedFamilyDetails` 
    const canShowReadOnlyCard = useMemo(
        () => !isNotFoundState && (hasPersistedFamilyDetails || hasFamilyData(familyDetails)),
        [familyDetails, hasPersistedFamilyDetails, isNotFoundState]
    );

    if (loading) {
        return (
            <InfoCard title="Family Details">
                <div className="flex justify-center items-center py-8 text-sm text-gray-600 dark:text-gray-300">
                    Loading...
                </div>
            </InfoCard>
        );
    }

    const isMarried = familyDetails?.maritalStatus === "Married";
    const canHaveChildrenReadOnly = ["Married", "Divorced", "Widowed"].includes(familyDetails?.maritalStatus || "");
    const emergencyContactNumber = renderPhoneNumber(
        joinPhoneNumber(familyDetails?.emergencyContactCountryCode, familyDetails?.emergencyContactNumber)
    );
    const children = canHaveChildrenReadOnly ? familyDetails?.children ?? [] : [];

    return (
        <>
            {canShowReadOnlyCard ? (
                <InfoCard
                    title="Family Details"
                    action={<EditButton onClick={handleEditMode} />}
                >
                    <InfoFieldGrid>
                        <InfoField label="Father Name" value={<span className="capitalize">{familyDetails?.fatherName}</span>} />
                        <InfoField label="Mother Name" value={<span className="capitalize">{familyDetails?.motherName}</span>} />
                        <InfoField label="Marital Status" value={familyDetails?.maritalStatus} />
                        <InfoField
                            label="Spouse Name"
                            value={isMarried ? <span className="capitalize">{familyDetails?.spouseName || "—"}</span> : "Not Applicable"}
                        />
                        <InfoField label="Emergency Contact Name" value={<span className="capitalize">{familyDetails?.emergencyContactName}</span>} />
                        <InfoField label="Emergency Contact Number" value={emergencyContactNumber} />
                        <InfoField label="Blood Group" value={<span className="uppercase">{familyDetails?.bloodGroup}</span>} />
                        <InfoField
                            label="Number of Children"
                            value={canHaveChildrenReadOnly ? String(familyDetails?.numberOfChildren ?? 0) : "Not Applicable"}
                        />
                    </InfoFieldGrid>

                    {children.length > 0 && (
                        <div className="mt-4 pt-2">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-gray-50 dark:bg-gray-800/60 border-y border-gray-100 dark:border-gray-700">
                                    <tr>
                                        <th scope="col" className={CHILD_TH_CLASS}>
                                            Child Name
                                        </th>
                                        <th scope="col" className={CHILD_TH_CLASS}>
                                            Child DOB
                                        </th>
                                        <th scope="col" className={CHILD_TH_CLASS}>
                                            Gender
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {children.map((child) => (
                                        <tr key={`${child.childName}-${child.childDob}-${child.gender}`}>
                                            <td className={`${CHILD_TD_CLASS} capitalize`}>
                                                {child.childName || "—"}
                                            </td>
                                            <td className={CHILD_TD_CLASS}>
                                                {child.childDob || "—"}
                                            </td>
                                            <td className={`${CHILD_TD_CLASS} capitalize`}>
                                                {child.gender || "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </InfoCard>
            ) : (
                <InfoCard title="Family Details">
                    <EmptyState message="No family details available">
                        <Button
                            onClick={handleAddMode}
                            type="button"
                            className="!bg-blue-600 hover:!bg-blue-700 !text-white shadow-md"
                        >
                            Add Family Details
                        </Button>
                    </EmptyState>
                </InfoCard>
            )}

            {(isEditing || isAdding) && (
                <Modal
                    isOpen={isEditing || isAdding}
                    onClose={handleCancel}
                    header={isAdding ? "Add Family Details" : "Edit Family Details"}
                    // custemHeight="700px"
                    // customWidth="700px"
                    size="md"
                >
                    <div className="family-details-form">
                        <FormProvider {...methods}>
                            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Input
                                        name="fatherName"
                                        label="Father Name"
                                        placeholder="Enter Father Name"
                                        required
                                        validationSchema={yup.string().trim().required("Father Name is required")}
                                        onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                                        onInput={handleNameInput}
                                    />
                                    <Input
                                        name="motherName"
                                        label="Mother Name"
                                        placeholder="Enter Mother Name"
                                        required
                                        validationSchema={yup.string().trim().required("Mother Name is required")}
                                        onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                                        onInput={handleNameInput}
                                    />

                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                                            Marital Status <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            {...register("maritalStatus")}
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        >
                                            <option value="" disabled hidden>
                                                Select Marital Status
                                            </option>
                                            <option value="Single">Single</option>
                                            <option value="Married">Married</option>
                                            <option value="Divorced">Divorced</option>
                                            <option value="Widowed">Widowed</option>
                                        </select>
                                        <div className="h-6 mt-1">
                                            {errors.maritalStatus?.message ? (
                                                <p className="text-red-500 text-xs font-medium leading-tight">
                                                    {errors.maritalStatus.message}
                                                </p>
                                            ) : (
                                                <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                                            Blood Group <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            {...register("bloodGroup")}
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        >
                                            <option value="" disabled hidden>
                                                Select Blood Group
                                            </option>
                                            {bloodGroupOptions.map((group) => (
                                                <option key={group} value={group}>
                                                    {group}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="h-6 mt-1">
                                            {errors.bloodGroup?.message ? (
                                                <p className="text-red-500 text-xs font-medium leading-tight">
                                                    {errors.bloodGroup.message}
                                                </p>
                                            ) : (
                                                <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                                            )}
                                        </div>
                                    </div>

                                    <Input
                                        name="emergencyContactName"
                                        label="Emergency Contact Name"
                                        placeholder="Enter Emergency Contact Name"
                                        required
                                        validationSchema={yup.string().trim().required("Emergency Contact Name is required")}
                                        onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                                        onInput={handleNameInput}
                                    />

                                    <PhoneNumberInput
                                        name="emergencyContactNumber"
                                        countryCodeName="emergencyContactCountryCode"
                                        label="Emergency Contact Number"
                                        placeholder="Enter mobile number"
                                        required
                                    />

                                    {shouldShowSpouseSection && (
                                        <Input
                                            name="spouseName"
                                            label="Spouse Name"
                                            placeholder="Enter Spouse Name"
                                            required
                                            validationSchema={yup.string().trim().required("Spouse Name is required")}
                                            onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                                            onInput={handleNameInput}
                                        />
                                    )}

                                    {canHaveChildren && (
                                        <Input
                                            name="numberOfChildren"
                                            label="Number Of Children"
                                            placeholder="Enter Number Of Children"
                                            required
                                            type="number"
                                            min={0}
                                            onKeyDown={preventSpecialCharKeyDownInTextfield}
                                            validationSchema={yup
                                                .number()
                                                .typeError("Number of Children must be a number")
                                                .min(0, "Number of Children cannot be negative")}
                                        />
                                    )}
                                </div>

                                {shouldShowChildrenSection && (
                                    <div className="mt-2">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Child Details</h3>
                                            <Button
                                                type="button"
                                                onClick={addChildRow}
                                                className="bg-blue-600 hover:bg-blue-700 text-white !px-3 !py-2 transition-colors"
                                            >
                                               Add Child
                                            </Button>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="space-y-4 overflow-x-hidden pb-1">
                                                {fields.map((field, index) => (
                                                    <div
                                                        key={field.id}
                                                        // Added w-full and slightly reduced base gap to prevent edge-cramping
                                                        className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-3 lg:items-start w-full"
                                                    >
                                                        {/* Added min-w-0 to all column wrappers to allow inputs to shrink without breaking the grid */}
                                                        <div className="lg:col-span-4 min-w-0">
                                                            <Input
                                                                name={`children.${index}.childName`}
                                                                label="Child Name"
                                                                placeholder="Enter Child Name"
                                                                required
                                                                compact
                                                                validationSchema={yup.string().trim().required("Child Name is required")}
                                                                onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                                                                onInput={handleNameInput}
                                                            />
                                                        </div>
                                                        <div className="lg:col-span-4 min-w-0">
                                                            <Input
                                                                name={`children.${index}.childDob`}
                                                                label="Child DOB"
                                                                type="date"
                                                                required
                                                                compact
                                                                max={maxDate}
                                                                validationSchema={yup.string().required("Child DOB is required")}
                                                            />
                                                        </div>
                                                        <div className="lg:col-span-3 min-w-0">
                                                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1 truncate">
                                                                Gender <span className="text-red-500">*</span>
                                                            </label>
                                                            <select
                                                                {...register(`children.${index}.gender` as const)}
                                                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                                            >
                                                                <option value="" disabled hidden>
                                                                    Select Gender
                                                                </option>
                                                                {genderOptions.map((option) => (
                                                                    <option key={option} value={option}>
                                                                        {option}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <div className="h-4 mt-0.5">
                                                                {errors.children?.[index]?.gender?.message ? (
                                                                    <p className="text-red-500 text-xs font-medium leading-tight">
                                                                        {errors.children[index]?.gender?.message}
                                                                    </p>
                                                                ) : (
                                                                    <p className="invisible text-xs font-medium leading-tight">
                                                                        placeholder
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="lg:col-span-1 min-w-0">
                                                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1 invisible">
                                                                Action
                                                            </label>
                                                            <div className="flex items-center lg:justify-center min-h-[42px]">
                                                                <Button
                                                                    type="button"
                                                                    onClick={() => removeChildRow(index)}
                                                                    disabled={fields.length === 0}
                                                                    className="!px-2 !py-1 !h-[32px] !min-w-[32px] !flex !items-center !justify-center shrink-0"
                                                                    color="red"
                                                                >
                                                                    -
                                                                </Button>
                                                            </div>
                                                            <div className="h-4 mt-0.5">
                                                                <p className="invisible text-xs font-medium leading-tight">placeholder</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={(!!familyDetails && !isDirty) || isSubmitting}
                                        className={`text-sm font-medium rounded-lg px-5 py-2.5 focus:ring-4 focus:outline-none focus:ring-blue-300 transition-colors ${(!!familyDetails && !isDirty) || isSubmitting
                                            ? "text-white bg-blue-400 cursor-not-allowed opacity-70"
                                            : "text-white bg-blue-700 hover:bg-blue-800"
                                            }`}
                                    >
                                        {familyDetails ? "Update" : "Add"}
                                    </button>
                                </div>
                            </form>
                        </FormProvider>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default FamilyDetails;